import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraEyebrow, SpecraHeading, SpecraIconTile, SpecraInlineBanner, SpecraKeyValueRow, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { agentClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
import { PROPOSAL_DECIDED_EVENT, recalledDecision, rememberDecision } from './proposalDecisions';
function publishErrorMessage(err) {
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.PermissionDenied:
                return err.rawMessage ? `${err.rawMessage.charAt(0).toUpperCase()}${err.rawMessage.slice(1)}.` : "You don't have access to do this here. Reject it, or ask someone who can.";
            case ConnectCode.NotFound:
            case ConnectCode.FailedPrecondition:
                return err.rawMessage || 'This can no longer be done. Ask the assistant again.';
            default:
                return "It couldn't be done right now. Retry, or reject it.";
        }
    }
    return "It couldn't be done right now. Retry, or reject it.";
}
/**
 * Asks before chat publishes anything shared or saved for the record — files
 * into the project's shared search, an analysis into Contract Intelligence.
 * Accept runs it as the user; Reject runs nothing.
 */ export function PublishCard({ output, toolCallId }) {
    const sendFeedback = useChatFeedback();
    // A decision taken in this session wins over the stream's stale output.
    const recalled = recalledDecision(toolCallId);
    const saved = recalled?.decision ?? output.approvalState;
    const [status, setStatus] = useState(saved === 'accepted' ? 'accepted' : saved === 'rejected' ? 'rejected' : 'pending');
    const [receipt, setReceipt] = useState(recalled?.receipt ?? output.receipt ?? '');
    useEffect(()=>{
        const onDecided = ()=>{
            const d = recalledDecision(toolCallId);
            if (!d) return;
            setStatus(d.decision);
            setReceipt(d.receipt ?? '');
        };
        window.addEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
        return ()=>window.removeEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
    }, [
        toolCallId
    ]);
    const [error, setError] = useState(null);
    const actingRef = useRef(false);
    const title = output.title || 'Proposed action';
    const items = output.items ?? [];
    const decidable = Boolean(output.approvalConversationId && toolCallId);
    if (output.success === false) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            variant: "framed",
            tone: "danger",
            "data-testid": Testids.assistant.publishCard,
            children: output.message || 'This could not be proposed.'
        });
    }
    const decide = async (decision)=>{
        if (actingRef.current || status !== 'pending' || !decidable) return;
        actingRef.current = true;
        setStatus('working');
        setError(null);
        try {
            const response = await agentClient.decideDocumentProposal({
                conversationId: output.approvalConversationId,
                toolCallId: toolCallId,
                decision
            });
            if (response.decision === 'rejected') {
                rememberDecision(toolCallId, {
                    decision: 'rejected'
                });
                setStatus('rejected');
                sendFeedback(`User declined: ${title}. Nothing was done. Ask what they want instead.`);
                return;
            }
            if (response.decision !== 'accepted') throw new Error('The decision could not be confirmed.');
            rememberDecision(toolCallId, {
                decision: 'accepted',
                receipt: response.receipt
            });
            setReceipt(response.receipt);
            setStatus('accepted');
            sendFeedback(`User accepted. Done: ${response.receipt || title}`);
        } catch (err) {
            setStatus('pending');
            setError(publishErrorMessage(err));
        } finally{
            actingRef.current = false;
        }
    };
    const accepted = status === 'accepted';
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        "data-testid": Testids.assistant.publishCard,
        "data-state": status,
        elevation: "xs",
        className: "overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3 px-4 pb-3 pt-3.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconTile, {
                        tone: accepted ? 'success' : 'info',
                        children: /*#__PURE__*/ _jsx(Share2, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: accepted ? 'Done' : 'Needs your OK · nothing done yet'
                            }),
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                size: "sm",
                                className: "mt-1",
                                children: title
                            })
                        ]
                    })
                ]
            }),
            output.destination || items.length ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 border-t border-border px-4 py-2.5",
                children: [
                    output.destination ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Where",
                        value: output.destination
                    }) : null,
                    items.length ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: items.length === 1 ? 'Item' : `${items.length} items`,
                        value: items.join(', ')
                    }) : null
                ]
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "danger",
                "data-testid": Testids.assistant.publishStatus,
                children: error
            }) : null,
            status === 'pending' || status === 'working' ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.assistant.publishAccept,
                        onClick: ()=>void decide('accepted'),
                        disabled: !decidable,
                        isLoading: status === 'working',
                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                            className: "h-4 w-4"
                        }),
                        children: output.acceptLabel || 'Accept'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.assistant.publishReject,
                        onClick: ()=>void decide('rejected'),
                        disabled: !decidable || status === 'working',
                        children: "Don't"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        className: "ml-auto",
                        children: decidable ? 'Nothing happens until you choose.' : 'This earlier proposal has no saved review status. Ask for it again.'
                    })
                ]
            }) : null,
            accepted ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.assistant.publishStatus,
                className: "flex items-start gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Check, {
                        className: "mt-0.5 h-3.5 w-3.5 shrink-0 text-success",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        className: "font-medium",
                        children: receipt || 'Done.'
                    })
                ]
            }) : null,
            status === 'rejected' ? /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.assistant.publishStatus,
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "micro",
                    tone: "muted",
                    children: "Not done — nothing changed."
                })
            }) : null
        ]
    });
}
